import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { fetchFreshness } from '@/api/commonApi';

const POLL_MS = 30000;

const ROUTE_FRESHNESS_KEY = {
  potential: 'potential',
  'in-progress': 'inprogress',
  'dq-files': 'inprogress',
  active: 'active',
  inactive: 'inactive'
};

function isNewer(next, prev) {
  if (!next) return false;
  if (!prev) return false;
  return String(next) > String(prev);
}

export function useFreshnessCheck({ enabled = true } = {}) {
  const route = useRoute();
  const toast = useToast();

  const staleTabs = ref([]);
  let timer = null;
  let lastEditBaseline = null;
  let warnedForRoute = '';

  async function poll() {
    try {
      const result = await fetchFreshness();

      const lastEdit = result.lastEdit || {};
      if (lastEditBaseline) {
        const stale = [];
        for (const [key, ts] of Object.entries(lastEdit)) {
          if (isNewer(ts, lastEditBaseline[key])) {
            stale.push(key);
          }
        }
        staleTabs.value = stale;

        const routeKey = ROUTE_FRESHNESS_KEY[route.name];
        if (routeKey && stale.includes(routeKey) && warnedForRoute !== route.fullPath) {
          warnedForRoute = route.fullPath;
          toast.add({
            severity: 'info',
            summary: 'Data updated',
            detail: 'Another user may have changed this list. Refresh to see the latest data.',
            life: 5000
          });
        }
      } else {
        staleTabs.value = [];
      }

      lastEditBaseline = { ...lastEdit };
    } catch {
      // Silent — no sidebar sync label; list tabs keep working offline of this poll.
    }
  }

  function start() {
    stop();
    poll();
    timer = setInterval(poll, POLL_MS);
  }

  function stop() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function acknowledgeCurrentTab() {
    warnedForRoute = route.fullPath;
    const routeKey = ROUTE_FRESHNESS_KEY[route.name];
    if (routeKey) {
      staleTabs.value = staleTabs.value.filter((k) => k !== routeKey);
    }
  }

  onMounted(() => {
    if (enabled) start();
  });

  onBeforeUnmount(() => {
    stop();
  });

  return {
    staleTabs,
    poll,
    start,
    stop,
    acknowledgeCurrentTab
  };
}
